import React, { useState, useEffect } from "react";
import { apiClient } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingBag, Heart, ArrowLeft, Bell, BellRing, Loader2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { createPageUrl, getOptimizedImageUrl, openExternalUrl } from "@/utils";
import { m, AnimatePresence } from "framer-motion";
import CategoryManager from "@/components/categories/CategoryManager";
import { useLikedProducts } from "@/hooks/useLikedProducts";

export default function ProductDetailPage() {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const { isLiked: isProductLiked, toggleLike: toggleProductLike } = useLikedProducts();

    const urlParams = new URLSearchParams(location.search);
    const productId = urlParams.get('id');

    // Fetch specific product by ID
    const { data: product, isLoading: isLoadingProduct } = useQuery({
        queryKey: ['product', productId],
        queryFn: () => apiClient.entities.Product.get(productId),
        enabled: !!productId
    });

    // Fetch variants if product has a group ID
    const { data: variants = [] } = useQuery({
        queryKey: ['variants', product?.variant_group_id],
        queryFn: () => apiClient.entities.Product.variants(product.variant_group_id),
        enabled: !!product?.variant_group_id && !product.variant_group_id.includes('single')
    });

    // Fetch similar products
    const { data: similarProducts = [], isLoading: isLoadingSimilar } = useQuery({
        queryKey: ['similarProducts', productId],
        queryFn: () => apiClient.entities.Recommendation.similar(productId, 5),
        enabled: !!productId
    });

    // Brand Subscription Queries and Mutations
    const { data: subscriptions = [], isLoading: isLoadingSubs } = useQuery({
        queryKey: ['brandSubscriptions'],
        queryFn: () => apiClient.notifications.getSubscriptions(),
        enabled: !!product?.site_id
    });

    const isSubscribed = subscriptions.some(sub => sub.id === product?.site_id);

    const subscribeMutation = useMutation({
        mutationFn: (siteId) => apiClient.notifications.subscribe(siteId),
        onSuccess: () => queryClient.invalidateQueries(['brandSubscriptions'])
    });

    const unsubscribeMutation = useMutation({
        mutationFn: (siteId) => apiClient.notifications.unsubscribe(siteId),
        onSuccess: () => queryClient.invalidateQueries(['brandSubscriptions'])
    });

    const handleToggleSubscription = () => {
        if (!product?.site_id) return;
        if (isSubscribed) {
            unsubscribeMutation.mutate(product.site_id);
        } else {
            subscribeMutation.mutate(product.site_id);
        }
    };

    // Scroll to top when productId changes
    useEffect(() => {
        window.scrollTo(0, 0);
        setSelectedImageIndex(0);
    }, [productId]);

    // Track View Duration
    const viewStartTimeRef = React.useRef(Date.now());
    useEffect(() => {
        viewStartTimeRef.current = Date.now();
        let recorded = false;
        const sendView = () => {
            if (!recorded && product) {
                const duration = (Date.now() - viewStartTimeRef.current) / 1000;
                if (duration > 0.2 && duration < 600) {
                    recorded = true;
                    apiClient.entities.UserInteraction.create({
                        product_id: product.id,
                        action: 'view_produit',
                        duration_seconds: Math.min(Math.max(1, Math.round(duration)), 90),
                        product: product
                    });
                }
            }
        };
        const onVisChange = () => { if (document.visibilityState === 'hidden') sendView(); };
        document.addEventListener('visibilitychange', onVisChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisChange);
            if (product) {
                const duration = (Date.now() - viewStartTimeRef.current) / 1000;
                // Minimum 0.5s, Max 1m30s (90s) to avoid AFK
                sendView();
                if (false && duration > 0.5 && duration < 90) {
                    apiClient.entities.UserInteraction.create({
                        product_id: product.id,
                        action: 'view_produit',
                        duration_seconds: Math.round(duration),
                        product: product
                    });
                }
            }
        };
    }, [product?.id]);

    const handleVisitSite = (e) => {
        apiClient.entities.UserInteraction.create({
            product_id: product.id,
            action: 'visit_marque_site'
        });
        if (Capacitor.isNativePlatform() && product?.product_url) {
            openExternalUrl(product.product_url, e);
        }
    };

    const isLiked = isProductLiked(product?.id);
    const handleLike = (e) => {
        if (product) {
            toggleProductLike(product, e);
        }
    };

    if (isLoadingProduct) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Chargement...</p>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Produit introuvable</h2>
                    <Button onClick={() => navigate('/search')}>Retour à la recherche</Button>
                </div>
            </div>
        );
    }

    const hasDiscount = product.original_price && product.original_price > product.price;

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50/40 via-stone-50 to-orange-50/30 dark:from-[hsl(228,14%,6%)] dark:via-[hsl(228,12%,8%)] dark:to-[hsl(225,10%,9%)] p-4 md:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Back Button */}
                <Button
                    variant="ghost"
                    className="mb-4 md:mb-6 pl-0 hover:bg-transparent hover:text-amber-600"
                    onClick={() => navigate(-1)}
                >
                    <ArrowLeft className="w-5 h-5 mr-1" />
                    Retour
                </Button>

                {/* Product Detail Layout */}
                <div className="bg-white dark:bg-[hsl(225,14%,10%)] rounded-2xl shadow-xl overflow-hidden">
                    <div className="grid md:grid-cols-2 gap-6 md:gap-8 p-4 md:p-8">
                        {/* Left: Images */}
                        <div className="space-y-4">
                            <div className="aspect-square rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
                                <img
                                    src={getOptimizedImageUrl(product.images[selectedImageIndex], 800)}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                    loading="eager"
                                />
                            </div>
                            {product.images.length > 1 && (
                                <div className="grid grid-cols-4 gap-2 md:gap-3">
                                    {product.images.map((img, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setSelectedImageIndex(idx)}
                                            className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${selectedImageIndex === idx
                                                ? 'border-amber-600 shadow-md ring-2 ring-amber-300 dark:ring-amber-900'
                                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                }`}
                                        >
                                            <img
                                                src={getOptimizedImageUrl(img, 150)}
                                                alt={`${product.name} ${idx + 1}`}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right: Details */}
                        <div className="flex flex-col space-y-5 md:space-y-6">
                            {/* Title */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <p
                                        className="text-sm font-medium text-amber-600 dark:text-amber-400 hover:underline cursor-pointer"
                                        onClick={() => { if (product.brand_slug) navigate(`/brand/${product.brand_slug}`); }}
                                    >
                                        {product.brand}
                                    </p>
                                    {product.site_id && (
                                        <div className="relative group/tooltip flex items-center">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={handleToggleSubscription}
                                                disabled={subscribeMutation.isPending || unsubscribeMutation.isPending || isLoadingSubs}
                                                className={`h-8 px-2 text-xs font-medium ${isSubscribed ? 'text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50 hover:bg-amber-100 dark:hover:bg-amber-950/70 hover:text-amber-900 dark:hover:text-amber-200' : 'text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30'}`}
                                            >
                                                {subscribeMutation.isPending || unsubscribeMutation.isPending ? (
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                                                ) : isSubscribed ? (
                                                    <BellRing className="w-3.5 h-3.5 mr-1.5" />
                                                ) : (
                                                    <Bell className="w-3.5 h-3.5 mr-1.5" />
                                                )}
                                                {isSubscribed ? 'Abonné' : "S'abonner"}
                                            </Button>
                                            
                                            {/* Tooltip */}
                                            <div className="absolute right-0 top-full mt-2 w-64 p-2.5 bg-gray-900 text-white text-xs rounded-md shadow-xl opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 z-50 pointer-events-none">
                                                <div className="absolute -top-1 right-6 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                                                <p className="font-semibold mb-1">Alerte Nouveautés</p>
                                                <p className="text-gray-300 leading-relaxed">Abonnez-vous à cette marque pour être notifié par email dès l'ajout de nouveaux articles.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white leading-tight">{product.name}</h1>
                                    {(product.stock_status === 'rupture' || !product.sizes || product.sizes.length === 0 || (product.sizes.length === 1 && !product.sizes[0]) || product.sizes.every(size => typeof size === 'string' && size.includes('(Out of Stock)'))) && (
                                        <Badge className="bg-gray-800 text-white border-white/20 hover:bg-gray-800 self-start">
                                            Rupture de stock
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            {/* Price & Like Button */}
                            <div className="flex items-center justify-between gap-3 sm:gap-4 py-1">
                                <div className="flex flex-wrap items-baseline gap-2.5 sm:gap-3 min-w-0">
                                    <span className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white shrink-0">
                                        {product.price}€
                                    </span>
                                    {hasDiscount && (
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className="text-lg md:text-xl text-gray-400 dark:text-gray-500 line-through">
                                                {product.original_price}€
                                            </span>
                                            <Badge className="bg-red-500 text-white hover:bg-red-500 font-semibold text-xs px-2 py-0.5 shadow-sm">
                                                -{Math.round((1 - product.price / product.original_price) * 100)}%
                                            </Badge>
                                        </div>
                                    )}
                                </div>

                                {/* Bouton Coup de Cœur épuré (icône seule) à droite du prix - Toujours visible et shrink-0 */}
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="outline"
                                    onClick={handleLike}
                                    aria-label={isLiked ? "Retirer des coups de cœur" : "Ajouter aux coups de cœur"}
                                    className={`h-11 w-11 sm:h-12 sm:w-12 shrink-0 rounded-xl flex items-center justify-center transition-all shadow-xs cursor-pointer ${
                                        isLiked
                                            ? "bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 shadow-red-500/10"
                                            : "bg-white dark:bg-zinc-800/80 border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-gray-300 hover:text-red-500 hover:border-red-200 hover:bg-red-50/50 dark:hover:bg-red-950/30"
                                    }`}
                                >
                                    <m.div
                                        animate={{ scale: isLiked ? [1, 1.35, 1] : [1, 0.85, 1] }}
                                        transition={{ duration: 0.25, type: "spring" }}
                                        className="flex items-center justify-center"
                                    >
                                        <Heart className={`w-5 h-5 sm:w-6 sm:h-6 ${isLiked ? "fill-red-500 text-red-500" : "text-gray-500 dark:text-gray-400 stroke-[2.2]"}`} />
                                    </m.div>
                                </Button>
                            </div>

                            {/* Catégories personnalisées */}
                            <div className="border-t pt-4">
                                <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Mes catégories</p>
                                <CategoryManager productId={product.id} />
                            </div>

                            {/* Variants */}
                            {variants.length > 0 && (
                                <div className="space-y-3">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        Variantes
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {variants.map(variant => {
                                            const isActive = variant.id === product.id;
                                            return (
                                                <button
                                                    key={variant.id}
                                                    onClick={() => !isActive && navigate(`/product?id=${variant.id}`, { replace: true })}
                                                    className={`w-14 h-16 md:w-16 md:h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${isActive
                                                        ? 'border-amber-600 shadow-md cursor-default'
                                                        : 'border-transparent hover:border-amber-400 dark:hover:border-amber-500 hover:shadow-md cursor-pointer opacity-80 hover:opacity-100'
                                                        }`}
                                                    title={variant.name}
                                                >
                                                    <img
                                                        src={getOptimizedImageUrl(variant.images[0], 112)}
                                                        alt={variant.name}
                                                        className="w-full h-full object-cover"
                                                        loading="lazy"
                                                    />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Buy Button */}
                            <div className="flex gap-3 pt-2">
                                {product.product_url ? (
                                    <a
                                        href={product.product_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1"
                                        onClick={handleVisitSite}
                                    >
                                        <Button
                                            size="lg"
                                            className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white text-base md:text-lg h-12 md:h-14 shadow-lg shadow-amber-600/20"
                                        >
                                            <ShoppingBag className="w-5 h-5 mr-2" />
                                            Acheter maintenant
                                        </Button>
                                    </a>
                                ) : (
                                    <Button
                                        size="lg"
                                        className="flex-1 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white text-base md:text-lg h-12 md:h-14 shadow-lg shadow-amber-600/20"
                                        disabled
                                    >
                                        <ShoppingBag className="w-5 h-5 mr-2" />
                                        Lien indisponible
                                    </Button>
                                )}
                            </div>

                            {/* Description */}
                            <div className="border-t pt-6">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 block">Description</h2>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-base md:text-lg opacity-90">{product.description}</p>
                            </div>

                            {/* Additional Info */}
                            <div className="space-y-4 border-t pt-6">
                                {product.materials && (
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Matériaux</p>
                                        <p className="text-gray-600 dark:text-gray-400">{product.materials}</p>
                                    </div>
                                )}

                                {product.sizes && product.sizes.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Tailles disponibles</p>
                                        <div className="flex flex-wrap gap-2">
                                            {product.sizes.map((size, idx) => (
                                                <Badge key={idx} variant="outline" className="dark:border-gray-700 dark:text-gray-300">{size}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vous aimerez aussi Section */}
                {similarProducts.length > 0 && (
                    <div className="mt-12 mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 px-2">Vous aimerez aussi</h2>
                        <div className="flex overflow-x-auto gap-4 pb-6 px-2 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            {similarProducts.map((p) => {
                                const isSimLiked = isProductLiked(p.id);
                                return (
                                    <Link 
                                        key={p.id} 
                                        to={`/product?id=${p.id}`}
                                        className="flex-none w-40 md:w-48 group cursor-pointer snap-start relative"
                                    >
                                        <div className="aspect-[3/4] rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 mb-3 relative shadow-sm group-hover:shadow-md transition-all">
                                            <img 
                                                src={getOptimizedImageUrl(p.images?.[0], 300) || 'https://via.placeholder.com/300x400?text=Image+Indisponible'} 
                                                alt={p.name} 
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                loading="lazy"
                                            />
                                            {p.original_price && p.original_price > p.price && (
                                                <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-md">
                                                    -{Math.round((1 - p.price / p.original_price) * 100)}%
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toggleProductLike(p, e);
                                                }}
                                                className={`absolute top-2 right-2 z-10 p-1.5 rounded-full backdrop-blur-md transition-all shadow-sm flex items-center justify-center ${
                                                    isSimLiked
                                                        ? "bg-white/95 dark:bg-zinc-900/95 text-red-500 ring-1 ring-red-500/30"
                                                        : "bg-black/35 text-white hover:bg-white/95 dark:hover:bg-zinc-900/95 hover:text-red-500"
                                                }`}
                                                title={isSimLiked ? "Retirer des coups de cœur" : "Ajouter aux coups de cœur"}
                                                aria-label={isSimLiked ? "Retirer des coups de cœur" : "Ajouter aux coups de cœur"}
                                            >
                                                <Heart
                                                    size={13}
                                                    className={`transition-colors duration-200 ${isSimLiked ? "fill-red-500 text-red-500" : "fill-transparent"}`}
                                                />
                                            </button>
                                        </div>
                                        <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate group-hover:text-amber-600 transition-colors">{p.name}</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{p.brand}</p>
                                        <div className="mt-1 flex items-center gap-2">
                                            <span className="font-bold text-sm text-gray-900 dark:text-white">{p.price}€</span>
                                            {p.original_price && p.original_price > p.price && (
                                                <span className="text-xs text-gray-400 line-through">{p.original_price}€</span>
                                            )}
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
